import React, { FC } from "react";

import { PartnerFormFraming } from "@xapp/stentor-form-widget-channel";

import styles from "./FormFrame.module.scss";

export interface FormFrameProps {
    /**
     * The partner chrome to draw. Without it nothing here renders anything but
     * the children.
     */
    readonly framing?: PartnerFormFraming;
    /**
     * Draw the frame and the tab.
     *
     * Off for a booking handoff step -- the partner's embed brings its own frame,
     * and a second one around it reads as a mistake.
     */
    readonly framed?: boolean;
    readonly children?: React.ReactNode;
}

/**
 * The partner framing around one form step: the tab off the top of the card, an
 * optional intro block carrying the PRESET's own copy, and the framed card itself.
 *
 * The intro deliberately does not fall back to the step's title. Step titles are
 * progress labels sized for a stepper -- "Request", "Contact Information" -- and
 * rendering one here turned it into a page-sized banner over the tab that nobody
 * authored. A preset that wants a headline says so with `framing.introTitle`.
 *
 * Only ever rendered when a partner style preset resolved, which only happens on
 * the stand-alone page -- see resolveFormStyle.
 */
export const FormFrame: FC<FormFrameProps> = (props) => {
    const { framing, framed, children } = props;

    const tabLabel = framing?.tabLabel;
    const title = framing?.introTitle;
    const subtitle = framing?.introSubtitle;
    const introPlacement = framing?.introPlacement || "above-tab";

    const intro =
        title || subtitle ? (
            <div className={styles.xapp_form_frame_intro} data-xapp-form-frame-intro="">
                {!!title && (
                    <h2 className={styles.xapp_form_frame_title} data-xapp-form-frame-title="">
                        {title}
                    </h2>
                )}
                {!!subtitle && (
                    <p className={styles.xapp_form_frame_subtitle} data-xapp-form-frame-subtitle="">
                        {subtitle}
                    </p>
                )}
            </div>
        ) : null;

    return (
        <div className={styles.xapp_form_frame_root}>
            {introPlacement === "above-tab" && intro}
            {/* The tab is a sibling ABOVE the card, not absolutely positioned inside it: in the
                approved design it is a tab sitting on the card's top edge (rounded top corners
                only, square bottom), and the card is pulled up to tuck under it. Centring a fully
                rounded pill over the border reads as a badge instead. The row keeps the intro's
                white background running behind the tab so the two read as one white block. */}
            {framed && !!tabLabel && (
                <div className={styles.xapp_form_frame_tab_row}>
                    <span className={styles.xapp_form_frame_tab} data-xapp-form-frame-tab="">
                        {tabLabel}
                    </span>
                </div>
            )}
            <div
                className={framed ? styles.xapp_form_frame_framed : styles.xapp_form_frame_plain}
                data-xapp-form-frame={framed ? "framed" : "plain"}
            >
                {introPlacement === "inline" && intro}
                {children}
            </div>
        </div>
    );
};

export default FormFrame;
